<template>
  <v-container>
    <!-- Výběr roku pomocí SelectYear komponenty -->
    <v-row class="mb-4 align-center justify-space-between">
      
      <v-col cols="12" sm="4" class="order-2 order-sm-1 text-sm-right text-center">
        <h3>
          {{ $t('payments.balance') }}:
          <span :style="{ color: totalFee < 0 ? 'red' : 'green' }">
            {{ totalFee.toLocaleString('cs-CZ') }} {{ currencySymbol(userCurrency) }}
          </span>
        </h3>
      </v-col>
      <v-col cols="12" sm="3" class="order-1 order-sm-2 text-sm-right">
        <SelectYear v-model="selectedYear" />
      </v-col>
    </v-row>

    <!-- Datová tabulka s údaji z eventu -->
    <v-data-table
      density="compact"
      :headers="headers"
      :items="payments"
      class="elevation-1"
      :items-per-page="50"
      loading-text="general.loading"
      :loading="loading" 
      hide-default-footer
      :sort-by="[{ key: 'event_date_start', order: 'asc' }]"
      no-data-text="payments.no_data"
      hover
      striped="even"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>
            {{ $formatShortDate(item.event_date_start) }}
            <span v-if="item.event_date_start !== item.event_date_end">&ndash;{{ $formatShortDate(item.event_date_end) }}
            </span> 
          </td>
          <td>
            <router-link :to="'/soutez/' + item.event_id">{{ item.event_name }}</router-link>
          </td>
          <td class="fee-cell">
            {{ item.fee_categories.toLocaleString('cs-CZ') }} {{ currencySymbol(item.user_currency) }}
            <span class="original" v-if="item.fee_categories_original && item.fee_categories_original !== item.fee_categories">{{ item.fee_categories_original.toLocaleString('cs-CZ') }}</span>
          </td>
          <td class="fee-cell">
            {{ item.fee_accommodation_food.toLocaleString('cs-CZ') }} {{ currencySymbol(item.user_currency) }}
            <span class="original" v-if="item.fee_accommodation_food_original && item.fee_accommodation_food_original !== item.fee_accommodation_food">{{ item.fee_accommodation_food_original.toLocaleString('cs-CZ') }}</span>
          </td>
          <td class="fee-cell">
            {{ item.fee_other_services.toLocaleString('cs-CZ') }} {{ currencySymbol(item.user_currency) }}
            <span class="original" v-if="item.fee_other_services_original && item.fee_other_services_original !== item.fee_other_services">{{ item.fee_other_services_original.toLocaleString('cs-CZ') }}</span>
          </td>
          <td class="fee-cell">
            {{ item.fee_automodule.toLocaleString('cs-CZ') }} {{ currencySymbol(item.user_currency) }}
            <span class="original" v-if="item.fee_automodule_original && item.fee_automodule_original !== item.fee_automodule">{{ item.fee_automodule_original.toLocaleString('cs-CZ') }}</span>
          </td>
          <td class="fee-cell">
            <strong :style="{ color: item.fee_summary < 0 ? 'red' : 'green' }">
              {{ item.fee_summary.toLocaleString('cs-CZ') }} {{ currencySymbol(item.user_currency) }}
            </strong>
            <span class="original" v-if="item.fee_summary_original && item.fee_summary_original !== item.fee_summary">&nbsp;{{ item.fee_summary_original.toLocaleString('cs-CZ') }}</span>
          </td>

        </tr>
      </template>

    </v-data-table>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import apiClient from '/api.js';
import SelectYear from '@/components/shared/SelectYear.vue'; 
import { useI18n } from 'vue-i18n'
import { currencySymbol } from '@/components/const/enums.js';


const { t } = useI18n()
const payments = ref([]);
const selectedYear = ref(new Date().getFullYear()); // Defaultně aktuální rok

// Sloupce tabulky
const headers = computed(() => [
  { title: t('payments.date'),                key: 'event_date_start' },
  { title: t('payments.event'),               key: 'event_name', minWidth: 180 },
  { title: t('payments.fee_categories'),      key: 'fee_categories', sortable: true },
  { title: t('payments.fee_accommodation_food'), key: 'fee_accommodation_food', sortable: true },
  { title: t('payments.fee_other_services'),  key: 'fee_other_services', sortable: true },
  { title: t('payments.fee_automodule'),      key: 'fee_automodule', sortable: true },
  { title: t('payments.fee_summary'),         key: 'fee_summary', minWidth: 150, sortable: true },
])
const loading = ref(true);

// Načtení dat pro vybraný rok
async function fetchPayments() {
  loading.value = true;
  try {
    const response = await apiClient.get('payment/', {
      params: { year: selectedYear.value }
    });
    payments.value = response.data;
  } catch (error) {
    console.error('Error fetching payments:', error);
  } finally {
    loading.value = false;  // Nastavíme loading na false po načtení dat
  }

}

const userCurrency = computed(() => payments.value[0]?.user_currency || 'CZK')

// Sleduj změnu roku a načítej data
watch(selectedYear, fetchPayments);

onMounted(fetchPayments);

// Celková částka
const totalFee = computed(() =>
  payments.value.reduce((sum, item) => sum + (item.fee_summary || 0), 0)
);
</script>

<style scoped>
.fee-cell .original {
  position: relative;
  display: inline-block;
  color: #666;
}

/* diagonal strike-through */
.fee-cell .original::before {
  content: "";
  position: absolute;
  width: 90%;      /* trošku delší, aby čára končila mimo okraj */
  height: 1px;      /* tloušťka čáry */
  background: currentColor;
  top: 45%;         /* ve vertikálním středu supu */
  left: 10%;       /* odsunutí doleva, aby čára začínala mimo */
  transform: rotate(-45deg);
  transform-origin: center;
}

</style>